import type { SpaEvent } from '@/api/types/telemetry';

const FLUSH_INTERVAL_MS = 30_000;
const BUFFER_THRESHOLD = 20;
const MAX_BUFFER_SIZE = 500;

let eventBuffer: SpaEvent[] = [];
let flushIntervalId: ReturnType<typeof setInterval> | null = null;
let initialized = false;

// ---------------------------------------------------------------------------
// Session ID helper (avoids circular dep with sessionReplay.ts)
// ---------------------------------------------------------------------------
function getSessionIdSafe(): string | undefined {
  try {
    return window.__endoSessionId;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// SPA event buffering & flushing
// ---------------------------------------------------------------------------
function bufferEvent(event: SpaEvent): void {
  eventBuffer.push(event);
  if (eventBuffer.length > MAX_BUFFER_SIZE) {
    eventBuffer = eventBuffer.slice(-MAX_BUFFER_SIZE);
  }
  if (eventBuffer.length >= BUFFER_THRESHOLD) {
    void flushEvents();
  }
}

async function flushEvents(): Promise<void> {
  if (eventBuffer.length === 0) return;

  const batch = [...eventBuffer];
  eventBuffer = [];

  try {
    const apiUrl = import.meta.env.VITE_API_URL;
    const token = localStorage.getItem('ea_access_token');
    if (!apiUrl || !token) {
      // Re-queue — no credentials available
      eventBuffer = [...batch, ...eventBuffer];
      return;
    }

    const res = await fetch(`${apiUrl}/telemetry/spa-events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
    if (!res.ok) {
      console.warn(`Telemetry flush failed: ${res.status}`);
      // Re-queue on server error (5xx), discard on client error (4xx)
      if (res.status >= 500) {
        eventBuffer = [...batch, ...eventBuffer];
      }
    }
  } catch {
    // Network error — re-queue
    eventBuffer = [...batch, ...eventBuffer];
  }

  // Enforce max buffer after re-queue
  if (eventBuffer.length > MAX_BUFFER_SIZE) {
    eventBuffer = eventBuffer.slice(-MAX_BUFFER_SIZE);
  }
}

// ---------------------------------------------------------------------------
// Error capture (only active after OTel is initialized)
// ---------------------------------------------------------------------------
export function reportError(error: Error | string, metadata?: Record<string, unknown>): void {
  if (!initialized) return;
  const message = typeof error === 'string' ? error : error.message;
  const stack = typeof error === 'string' ? undefined : error.stack;
  bufferEvent({
    type: 'error',
    message,
    timestamp: new Date().toISOString(),
    sessionId: getSessionIdSafe(),
    url: window.location.href,
    metadata: { ...metadata, stack },
  });
}

// ---------------------------------------------------------------------------
// Deferred setup
// ---------------------------------------------------------------------------
async function activateTelemetry(): Promise<void> {
  // Dynamic imports keep the initial bundle smaller.
  const [
    { WebTracerProvider, BatchSpanProcessor },
    { resourceFromAttributes },
    { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION },
    { FetchInstrumentation },
    { XMLHttpRequestInstrumentation },
    { DocumentLoadInstrumentation },
    { registerInstrumentations },
    { W3CTraceContextPropagator, ExportResultCode },
    { propagation, trace },
  ] = await Promise.all([
    import('@opentelemetry/sdk-trace-web'),
    import('@opentelemetry/resources'),
    import('@opentelemetry/semantic-conventions'),
    import('@opentelemetry/instrumentation-fetch'),
    import('@opentelemetry/instrumentation-xml-http-request'),
    import('@opentelemetry/instrumentation-document-load'),
    import('@opentelemetry/instrumentation'),
    import('@opentelemetry/core'),
    import('@opentelemetry/api'),
  ]);

  const apiUrl = import.meta.env.VITE_API_URL ?? '';
  const escApiUrl = apiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Custom exporter: converts OTel spans → SpaEvent and buffers them
  const exporter = {
    export(
      spans: Array<{ name: string; startTime: [number, number]; duration: [number, number]; spanContext: () => { traceId: string; spanId: string }; parentSpanContext?: { spanId?: string }; kind: number; status: { code: number }; attributes: Record<string, unknown> }>,
      resultCallback: (result: { code: number }) => void,
    ): void {
      for (const span of spans) {
        bufferEvent({
          type: 'navigation',
          message: span.name,
          timestamp: new Date(span.startTime[0] * 1000 + span.startTime[1] / 1e6).toISOString(),
          sessionId: getSessionIdSafe(),
          traceparent: span.spanContext().traceId,
          url: (span.attributes['http.url'] as string | undefined)
            ?? (span.attributes['url.full'] as string | undefined),
          metadata: {
            traceId: span.spanContext().traceId,
            spanId: span.spanContext().spanId,
            parentSpanId: span.parentSpanContext?.spanId ?? undefined,
            kind: span.kind,
            status: span.status.code,
            durationMs: (span.duration[0] * 1000 + span.duration[1] / 1e6).toFixed(2),
            attributes: Object.fromEntries(
              Object.entries(span.attributes).map(([k, v]) => [k, String(v)]),
            ),
          },
        });
      }
      resultCallback({ code: ExportResultCode.SUCCESS });
    },
    shutdown(): Promise<void> {
      return Promise.resolve();
    },
  };

  propagation.setGlobalPropagator(new W3CTraceContextPropagator());

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: 'EndoAxisSpa',
    [ATTR_SERVICE_VERSION]: '1.0.0',
  });

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(exporter, {
        maxQueueSize: 100,
        maxExportBatchSize: 20,
        scheduledDelayMillis: FLUSH_INTERVAL_MS,
      }),
    ],
  });

  provider.register();

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        ignoreUrls: [
          new RegExp(`${escApiUrl}/telemetry/`),
        ],
        propagateTraceHeaderCorsUrls: [new RegExp(escApiUrl)],
        clearTimingResources: true,
      }),
      new XMLHttpRequestInstrumentation({
        ignoreUrls: [
          new RegExp(`${escApiUrl}/telemetry/`),
        ],
        propagateTraceHeaderCorsUrls: [new RegExp(escApiUrl)],
      }),
      new DocumentLoadInstrumentation(),
    ],
  });

  // Error listeners
  window.addEventListener('error', (event) => {
    const activeSpan = trace.getActiveSpan();
    const err = event.error instanceof Error ? event.error : new Error(event.message);
    bufferEvent({
      type: 'error',
      message: err.message,
      timestamp: new Date().toISOString(),
      sessionId: getSessionIdSafe(),
      traceparent: activeSpan?.spanContext().traceId ?? undefined,
      url: window.location.href,
      metadata: { stack: err.stack, filename: event.filename, lineno: event.lineno, colno: event.colno },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    bufferEvent({
      type: 'error',
      message: reason.message,
      timestamp: new Date().toISOString(),
      sessionId: getSessionIdSafe(),
      url: window.location.href,
      metadata: { stack: reason.stack, type: 'unhandledrejection' },
    });
  });

  // Web vitals
  import('web-vitals').then(({ onLCP, onCLS, onTTFB, onINP }) => {
    const report = (name: string) => (metric: { value: number; rating: string }) => {
      bufferEvent({
        type: 'vital',
        message: name,
        timestamp: new Date().toISOString(),
        sessionId: getSessionIdSafe(),
        url: window.location.href,
        metadata: { value: metric.value, rating: metric.rating },
      });
    };
    onLCP(report('LCP'));
    onCLS(report('CLS'));
    onTTFB(report('TTFB'));
    onINP(report('INP'));
  }).catch(() => {
    // web-vitals unavailable — non-critical
  });

  // Periodic flush
  flushIntervalId = setInterval(() => void flushEvents(), FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushEvents();
    }
  });

  initialized = true;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export function initTelemetry(): void {
  if (initialized) return;
  void activateTelemetry();
}

export function stopTelemetry(): void {
  if (flushIntervalId) {
    clearInterval(flushIntervalId);
    flushIntervalId = null;
  }
  if (initialized) {
    void flushEvents();
  }
}
